import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Gift, Trash2, RefreshCw, Filter, Scissors } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from './shared';

/**
 * Promo Credits manager — list every credit, filter, bulk delete, "keep
 * only most recent N" quick action.
 *
 * Used credits cannot be deleted (they reflect actual financial activity on
 * real orders).
 */
const PromosTab = ({ token }) => {
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // 'all' | 'active' | 'used' | 'expired'
  const [selected, setSelected] = useState(new Set());
  const [keepN, setKeepN] = useState('29');
  const [search, setSearch] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCredits = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/promo-credits?include_used=true`, { headers });
      setCredits(res.data || []);
    } catch (e) {
      toast.error('Failed to load promo credits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCredits(); }, []); // eslint-disable-line

  const now = new Date().toISOString();
  const filtered = useMemo(() => {
    let list = [...credits];
    if (filter === 'active') {
      list = list.filter((c) => !c.used_at && (!c.expires_at || c.expires_at > now));
    } else if (filter === 'used') {
      list = list.filter((c) => !!c.used_at);
    } else if (filter === 'expired') {
      list = list.filter((c) => !c.used_at && c.expires_at && c.expires_at <= now);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((c) =>
        (c.user_email || '').toLowerCase().includes(q) ||
        (c.reason || '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [credits, filter, search, now]);

  const counts = useMemo(() => ({
    all: credits.length,
    active: credits.filter((c) => !c.used_at && (!c.expires_at || c.expires_at > now)).length,
    used: credits.filter((c) => !!c.used_at).length,
    expired: credits.filter((c) => !c.used_at && c.expires_at && c.expires_at <= now).length,
  }), [credits, now]);

  const toggleAll = (checked) => {
    const next = new Set(selected);
    filtered.forEach((c) => {
      if (c.used_at) return;
      if (checked) next.add(c.id);
      else next.delete(c.id);
    });
    setSelected(next);
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) { toast.error('Select at least one credit'); return; }
    if (!window.confirm(`Delete ${ids.length} unused credit${ids.length !== 1 ? 's' : ''}? Used credits in your selection (if any) will be skipped.`)) return;
    try {
      const res = await axios.post(`${API_URL}/admin/promo-credits/bulk-delete`, { ids }, { headers });
      toast.success(`Deleted ${res.data.deleted} credit${res.data.deleted !== 1 ? 's' : ''}`);
      setSelected(new Set());
      fetchCredits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk delete failed');
    }
  };

  const handleKeepRecent = async () => {
    const n = parseInt(keepN, 10);
    if (isNaN(n) || n < 0) { toast.error('Enter a number ≥ 0'); return; }
    if (!window.confirm(`Keep only the ${n} most recent UNUSED credits and delete the rest? Used credits are not touched.`)) return;
    try {
      const res = await axios.post(`${API_URL}/admin/promo-credits/keep-recent`, { keep: n }, { headers });
      toast.success(`Kept ${res.data.kept} most recent, deleted ${res.data.deleted}`);
      setSelected(new Set());
      fetchCredits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cleanup failed');
    }
  };

  const handleSingleDelete = async (credit) => {
    if (credit.used_at) { toast.error('Cannot delete a redeemed credit'); return; }
    if (!window.confirm(`Revoke this credit?\n\n${credit.user_email}\n${credit.reason || ''}`)) return;
    try {
      await axios.delete(`${API_URL}/admin/promo-credits/${credit.id}`, { headers });
      toast.success('Credit revoked');
      fetchCredits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke');
    }
  };

  const stateLabel = (c) => {
    if (c.used_at) return { label: 'Redeemed', cls: 'bg-purple-100 text-purple-700' };
    if (c.expires_at && c.expires_at <= now) return { label: 'Expired', cls: 'bg-gray-200 text-gray-600' };
    return { label: 'Active', cls: 'bg-green-100 text-green-700' };
  };

  return (
    <div className="space-y-4" data-testid="promos-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Gift className="w-6 h-6 text-[#FF6B35]" />
          <h2 className="text-lg font-semibold">Promo Credits ({counts.all})</h2>
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <Button onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="bulk-delete-promos-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Delete {selected.size} selected
            </Button>
          )}
          <Button onClick={fetchCredits} variant="outline" size="sm" data-testid="refresh-promos-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Keep-most-recent quick action */}
      <div className="bg-blue-50 border border-blue-200 rounded p-4 flex items-center gap-3 flex-wrap" data-testid="keep-recent-card">
        <Scissors className="w-5 h-5 text-blue-700 flex-shrink-0" />
        <div className="flex-1 min-w-[240px]">
          <p className="font-medium text-blue-900 text-sm">Quick cleanup</p>
          <p className="text-xs text-blue-700">Keep only the most recently-granted unused credits and delete everything older. Used credits are never touched.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-blue-900">Keep most recent</span>
          <Input
            type="number"
            value={keepN}
            onChange={(e) => setKeepN(e.target.value)}
            className="w-20"
            data-testid="keep-n-input"
          />
          <Button onClick={handleKeepRecent} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="keep-recent-btn">
            Apply
          </Button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { id: 'all', label: 'All' },
          { id: 'active', label: 'Active' },
          { id: 'used', label: 'Redeemed' },
          { id: 'expired', label: 'Expired' },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => { setFilter(f.id); setSelected(new Set()); }}
            className={`px-3 py-1 rounded-full text-sm border ${filter === f.id ? 'bg-[#0A2342] text-white border-[#0A2342]' : 'bg-white hover:bg-gray-50'}`}
            data-testid={`filter-${f.id}`}
          >
            {f.label} ({counts[f.id]})
          </button>
        ))}
        <Input
          placeholder="Search by email or reason…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs ml-auto"
          data-testid="promo-search"
        />
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0A2342] text-white">
            <tr>
              <th className="px-2 py-3 w-10 text-center">
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && filtered.filter(c => !c.used_at).every(c => selected.has(c.id))}
                  onChange={(e) => toggleAll(e.target.checked)}
                  data-testid="select-all-promos"
                />
              </th>
              <th className="px-4 py-3 text-left">User</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Reason / Note</th>
              <th className="px-4 py-3 text-center">State</th>
              <th className="px-4 py-3 text-center">Granted</th>
              <th className="px-4 py-3 text-center">Expires</th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-500">No credits match this filter.</td></tr>
            ) : filtered.map((c) => {
              const meta = stateLabel(c);
              const isRedeemed = !!c.used_at;
              return (
                <tr key={c.id} className="border-b hover:bg-gray-50" data-testid={`promo-row-${c.id}`}>
                  <td className="px-2 py-3 text-center">
                    {!isRedeemed && (
                      <input
                        type="checkbox"
                        checked={selected.has(c.id)}
                        onChange={() => toggleOne(c.id)}
                        data-testid={`select-promo-${c.id}`}
                      />
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium">{c.user_email || c.user_id}</td>
                  <td className="px-4 py-3">
                    {c.type === 'free_item'
                      ? <span><span className="text-[#FF6B35] font-semibold">Free item</span></span>
                      : <span><span className="text-[#FF6B35] font-semibold">R{Number(c.value).toLocaleString()}</span> off{c.applies_to_category ? ` (${c.applies_to_category})` : ''}</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={c.reason}>{c.reason || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${meta.cls}`}>{meta.label}</span>
                    {isRedeemed && c.discount_value && <div className="text-xs text-gray-500 mt-1">−R{Number(c.discount_value).toLocaleString()}</div>}
                  </td>
                  <td className="px-4 py-3 text-center text-xs text-gray-500">{c.created_at ? new Date(c.created_at).toLocaleDateString('en-ZA') : '—'}</td>
                  <td className="px-4 py-3 text-center text-xs text-gray-500">{c.expires_at ? new Date(c.expires_at).toLocaleDateString('en-ZA') : 'No expiry'}</td>
                  <td className="px-4 py-3 text-center">
                    {!isRedeemed && (
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleSingleDelete(c)} data-testid={`revoke-promo-${c.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PromosTab;
